import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ideasService } from "../service/api";
import { apiService } from "@/core/api";
import { API_ENDPOINTS } from "@/core/constants/endpoints";
import { useToast } from "@/core/hooks/useToast";
import { setModalIsOpened } from "@/core/store/app";
import {
  fetchIdeaDetailRequested,
} from "../store";
import { updateCallBookmarkFx } from "@/features/calls/store";
import { AxiosRequestConfig } from "axios";

export const useIdeaActionsViewModel = (
  ideaId: string | undefined,
  userId: string | undefined
) => {
  const navigate = useNavigate();
  const { showError, showSuccess } = useToast();
  const [requestLoading, setRequestLoading] = useState(false);
  const [publishLoading, setPublishLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [archiveLoading, setArchiveLoading] = useState(false);

  const handleBookmarkCall = useCallback(
    async (callId: string, isBookmarked: boolean) => {
      try {
        await updateCallBookmarkFx({ callId, isBookmarked: !isBookmarked });
        if (ideaId) {
          fetchIdeaDetailRequested(ideaId);
        }
      } catch (error: any) {
        console.error("Failed to bookmark call:", error);
        showError(error?.message || "Failed to update bookmark");
      }
    },
    [ideaId, showError]
  );

  const leaveIdea = useCallback(async () => {
    if (!ideaId) return;

    try {
      setRequestLoading(true);
      await ideasService.leaveIdea(ideaId);
      fetchIdeaDetailRequested(ideaId);
    } catch (error: any) {
      console.error("Failed to leave idea:", error);
      showError(error?.error || "Failed to leave idea");
    } finally {
      setRequestLoading(false);
    }
  }, [ideaId, showError]);

  const requestToJoin = useCallback(async () => {
    if (!userId || !ideaId) return;

    try {
      setRequestLoading(true);
      await ideasService.requestJoinIdea(ideaId, userId, "request");
      fetchIdeaDetailRequested(ideaId);
    } catch (error: any) {
      console.error("Failed to request to join:", error);
      showError(error || "Failed to request to join");
    } finally {
      setRequestLoading(false);
    }
  }, [ideaId, userId, showError]);

  const withdrawRequest = useCallback(async () => {
    if (!ideaId || !userId) return;

    try {
      setRequestLoading(true);
      await ideasService.withdrawJoinIdea(ideaId, userId, "request", "remove");
      fetchIdeaDetailRequested(ideaId);
    } catch (error: any) {
      console.error("Failed to withdraw request:", error);
      showError(error?.error || "Failed to withdraw request");
    } finally {
      setRequestLoading(false);
    }
  }, [ideaId, userId, showError]);

  const publishIdea = useCallback(async () => {
    if (!ideaId) return;

    try {
      setPublishLoading(true);
      await ideasService.publishIdea(ideaId);
      showSuccess("Idea published successfully");
      fetchIdeaDetailRequested(ideaId);
    } catch (error: any) {
      console.error("Failed to publish idea:", error);
      showError(error?.error || "Failed to publish idea");
    } finally {
      setPublishLoading(false);
    }
  }, [ideaId, showError, showSuccess]);

  const restoreIdea = useCallback(async () => {
    if (!ideaId) return;

    try {
      setRestoreLoading(true);
      await ideasService.restoreIdea(ideaId);
      showSuccess("Idea restored successfully");
      fetchIdeaDetailRequested(ideaId);
    } catch (error: any) {
      console.error("Failed to restore idea:", error);
      showError(error?.error || "Failed to restore idea");
    } finally {
      setRestoreLoading(false);
    }
  }, [ideaId, showError, showSuccess]);

  const archiveIdea = useCallback(async () => {
    if (!ideaId) return;

    try {
      setArchiveLoading(true);
      const patchConfig: AxiosRequestConfig = {
        headers: { "Content-Type": "application/json" },
      };
      await apiService.patch(
        API_ENDPOINTS.IDEAS.IDEA_STATUS_ACTION(ideaId, "archive"),
        patchConfig
      );
      showSuccess("Idea archived successfully");
      fetchIdeaDetailRequested(ideaId);
    } catch (error: any) {
      console.error("Failed to archive idea:", error);
      showError(error?.error || "Failed to archive idea");
    } finally {
      setArchiveLoading(false);
    }
  }, [ideaId, showError, showSuccess]);

  const removeMember = useCallback(
    async (memberId: string) => {
      if (!ideaId) return;

      try {
        await apiService.patch(API_ENDPOINTS.IDEAS.REMOVE_MEMBER, {
          idea_id: parseInt(ideaId),
          user_id: parseInt(memberId),
        });
        fetchIdeaDetailRequested(ideaId);
      } catch (error: any) {
        console.error("Failed to remove member:", error);
        showError(error?.error || "Failed to remove member");
      }
    },
    [ideaId, showError]
  );

  const makeCoordinator = useCallback(
    async (partnerId: string) => {
      if (!ideaId) return;

      try {
        await apiService.patch(API_ENDPOINTS.IDEAS.MAKE_COORDINATOR, {
          idea_id: parseInt(ideaId),
          user_id: partnerId,
        });
        fetchIdeaDetailRequested(ideaId);
        showSuccess("Partner made coordinator successfully");
      } catch (error: any) {
        console.error("Failed to make coordinator:", error);
        showError(error?.error || "Failed to make coordinator");
      }
    },
    [ideaId, showError, showSuccess]
  );

  return {
    requestLoading,
    publishLoading,
    restoreLoading,
    archiveLoading,
    handleBookmarkCall,
    leaveIdea,
    requestToJoin,
    withdrawRequest,
    publishIdea,
    restoreIdea,
    archiveIdea,
    removeMember,
    makeCoordinator,
  };
};
